import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/brand";
import ArtCard from "../components/ArtCard";
import { Sparkles, Bell } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_META = {
  music: {
    title: "Music",
    overline: "Sounds of the isle",
    body: "Acoustic ballads, drones, and slow airs — recorded in the studio, released directly.",
    soonHint: "New ballads in the works. Subscribe below to be first to hear a release.",
  },
  photography: {
    title: "Photography",
    overline: "Captured light",
    body: "Land, mist and ancient woods. Limited print runs from the Grá camera.",
    soonHint: "The first photo series is being curated. Check back soon for the inaugural release.",
  },
  drawing: {
    title: "Visual Artwork",
    overline: "Drawings & paintings",
    body: "Original drawings and paintings — hand-rendered, signed, and released directly from the studio.",
    soonHint: "Fresh pieces on the easel. Drop your email below and we'll let you know when they go live.",
  },
};

function ComingSoonPlaceholder({ meta }) {
  return (
    <div
      data-testid="coming-soon-placeholder"
      className="relative overflow-hidden border border-[#1A2B20] bg-[#09120C]"
    >
      {/* decorative knot */}
      <img
        src={`${API}/brand/knot.png`}
        alt=""
        aria-hidden="true"
        className="absolute -right-20 -bottom-24 w-96 h-96 object-contain opacity-[0.10] pointer-events-none select-none"
      />
      <div className="relative px-8 md:px-16 py-20 md:py-28 max-w-2xl">
        <div className="inline-flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#279153]" />
          <Sparkles className="w-4 h-4 text-[#279153]" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-[#279153]">
            Ag teacht — Coming Soon
          </span>
        </div>
        <h2 className="font-serif-display text-5xl md:text-6xl text-white leading-[1.05] mb-6">
          The studio is<br />
          <em className="not-italic font-medium text-[#279153]">still creating</em>.
        </h2>
        <p className="text-base md:text-lg text-[#95A89C] leading-relaxed mb-10">
          {meta.soonHint}
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/contact"
            data-testid="coming-soon-notify-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase transition-colors"
          >
            <Bell className="w-4 h-4" /> Notify me
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white text-xs tracking-[0.3em] uppercase hover:border-[#279153] hover:text-[#279153] transition-colors"
          >
            The story
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Gallery({ type }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = TYPE_META[type] || {};

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products`, { params: { type } })
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div data-testid={`gallery-${type}`} className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-12">
      <div className="border-b border-[#1A2B20] pb-10 mb-12">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">{meta.overline}</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl text-white mb-4">{meta.title}</h1>
        <p className="text-base text-[#95A89C] max-w-2xl">{meta.body}</p>
      </div>

      {loading ? (
        <div data-testid="gallery-loading" className="text-[#95A89C] text-sm">Loading the collection…</div>
      ) : products.length === 0 ? (
        <ComingSoonPlaceholder meta={meta} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p) => <ArtCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
